import { UntypedFormGroup } from '@angular/forms';
import { NumberFormatSeparator } from 'enums-lib';
import {
  DecimalFormatOptions,
  IntegerFormatOptions,
  SYSTEM_DEFAULT_DECIMAL_FORMAT,
  SYSTEM_DEFAULT_INTEGER_FORMAT,
} from 'interfaces-lib';

export interface INumberFormatControls {
  numberDecimalPlaces: number;
  numberDecimalSeparator: NumberFormatSeparator;
  numberThousandsSeparator: NumberFormatSeparator;
}

/**
 * Seeds the three form controls from the stored config, falling back to the same system defaults
 * the Word add-in applies when no corp config exists, so a corp that has never saved sees the
 * values its fields were already getting.
 *
 * Both stored objects carry a thousands separator, but only `decimalFormat` carries all three
 * values, so it is the single source read here. That keeps the two objects from drifting apart
 * if something ever wrote them inconsistently.
 */
export function numberFormatToControls(decimalFormat?: Partial<DecimalFormatOptions>): INumberFormatControls {
  return {
    numberDecimalPlaces: decimalFormat?.exactDecimalDigits ?? SYSTEM_DEFAULT_DECIMAL_FORMAT.exactDecimalDigits,
    numberDecimalSeparator: decimalFormat?.decimalSeparator ?? SYSTEM_DEFAULT_DECIMAL_FORMAT.decimalSeparator,
    numberThousandsSeparator: decimalFormat?.thousandsSeparator ?? SYSTEM_DEFAULT_DECIMAL_FORMAT.thousandsSeparator,
  };
}

/**
 * Builds the integer options the add-in consumes. Integers have no decimal places, so only the
 * thousands separator carries over from the corp settings.
 *
 * `minimumIntegerDigits` has no UI anywhere: the add-in's format panels reject anything other than
 * the system default, so it is taken from there rather than exposed as a corp setting.
 */
export function controlsToIntegerFormat(fieldOptionsGroup: UntypedFormGroup): IntegerFormatOptions {
  return {
    minimumIntegerDigits: SYSTEM_DEFAULT_INTEGER_FORMAT.minimumIntegerDigits,
    thousandsSeparator: fieldOptionsGroup.get('numberThousandsSeparator').value,
  };
}

/**
 * Builds the decimal options the add-in consumes, used for both double and bigdecimal fields.
 */
export function controlsToDecimalFormat(fieldOptionsGroup: UntypedFormGroup): DecimalFormatOptions {
  return {
    minimumIntegerDigits: SYSTEM_DEFAULT_DECIMAL_FORMAT.minimumIntegerDigits,
    thousandsSeparator: fieldOptionsGroup.get('numberThousandsSeparator').value,
    exactDecimalDigits: fieldOptionsGroup.get('numberDecimalPlaces').value,
    decimalSeparator: fieldOptionsGroup.get('numberDecimalSeparator').value,
  };
}
